import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, MessageCircle, Check } from "lucide-react";
import { copyToClipboard } from "@/utils/clipboard";
import { toast } from "sonner";

interface ShareLinkFallbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string;
  message?: string;
  title?: string;
}

/**
 * Fallback shown when programmatic clipboard copy fails (typical on Safari
 * after multiple awaits). The supplier can copy the link manually or open
 * WhatsApp directly.
 */
const ShareLinkFallbackModal = ({
  open,
  onOpenChange,
  url,
  message,
  title = "Seu link está pronto",
}: ShareLinkFallbackModalProps) => {
  const [copied, setCopied] = useState<null | "link" | "message">(null);

  useEffect(() => {
    if (!open) setCopied(null);
  }, [open]);

  const handleCopy = async (text: string, kind: "link" | "message") => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(kind);
      toast.success(kind === "link" ? "Link copiado!" : "Mensagem copiada!");
    } else {
      toast.error("Não foi possível copiar automaticamente. Selecione o texto e copie.");
    }
  };

  const whatsappHref = message
    ? `https://wa.me/?text=${encodeURIComponent(message)}`
    : `https://wa.me/?text=${encodeURIComponent(url)}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Copie o link abaixo ou envie direto pelo WhatsApp.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Link</label>
            <div className="flex gap-2">
              <input
                readOnly
                value={url}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 h-10 rounded-md border border-input bg-muted/40 px-3 text-sm truncate"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopy(url, "link")}
                className="shrink-0"
              >
                {copied === "link" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {message && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">
                Mensagem completa (WhatsApp)
              </label>
              <textarea
                readOnly
                value={message}
                onFocus={(e) => e.currentTarget.select()}
                rows={5}
                className="w-full rounded-md border border-input bg-muted/40 p-3 text-sm resize-none"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopy(message, "message")}
                className="w-full"
              >
                {copied === "message" ? (
                  <><Check className="h-4 w-4 mr-2" /> Mensagem copiada</>
                ) : (
                  <><Copy className="h-4 w-4 mr-2" /> Copiar mensagem</>
                )}
              </Button>
            </div>
          )}

          <Button
            asChild
            className="w-full rounded-full bg-gradient-to-r from-[#FF5E0E] to-[#7C3AED] text-white border-0 hover:opacity-90"
          >
            <a href={whatsappHref} target="_blank" rel="noreferrer">
              <MessageCircle className="h-4 w-4 mr-2" />
              Abrir no WhatsApp
            </a>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareLinkFallbackModal;
